// Conservation des données (RGPD art. 5.1.e — limitation de la conservation), 15/10/2026 : durées par défaut, planchers et
// fondements, partagés par le serveur (qui purge) et l'interface (qui affiche et règle).
//
// Règles :
//  - chaque société règle ses durées dans Administration › Conservation des données, JAMAIS en dessous du plancher documenté
//    ici (le serveur refuse ; la base elle-même ramène toute valeur hors bornes dans les bornes : migration 033,
//    trame_conservation_duree — un test vérifie que les deux tables sont identiques) ;
//  - la durée des engagements du représentant légal n'est PAS réglable par la société : c'est la durée de preuve de l'éditeur
//    (responsable de traitement pour les données du signataire), fin du contrat + 5 ans ;
//  - la démonstration (données marquées « Démo ») n'est jamais purgée.

export type CleDuree = 'historique_ans' | 'temps_ans' | 'connexion_mois' | 'comptes_mois' | 'bons_ans';

export interface DefinitionDuree {
  cle: CleDuree;
  libelle: string;
  unite: 'ans' | 'mois';
  defaut: number;
  min: number;
  max: number;
  /** Point de départ du délai. */
  depart: string;
  /** Ce qui se passe à l'échéance. */
  effet: string;
  /** Pourquoi ce plancher (texte de loi ou recommandation). */
  fondement: string;
}

export const DUREES: Record<CleDuree, DefinitionDuree> = {
  historique_ans: {
    cle: 'historique_ans', libelle: 'Historique des chantiers et photos', unite: 'ans', defaut: 10, min: 10, max: 30,
    depart: 'la réception des travaux',
    effet: 'Photos effacées (fichier et ligne), consignes de relève et planning effacés ; ce qui a été fait (coches, mesures, réserves, '
      + 'pièces posées) est gardé pour l’historique de l’installation, attribué à « Ancien salarié ».',
    fondement: 'Garantie décennale : dix ans à compter de la réception (Code civil, articles 1792 et 1792-4-1). Sans date de '
      + 'réception, rien n’est effacé.',
  },
  temps_ans: {
    cle: 'temps_ans', libelle: 'Temps passé déclaré (nominatif)', unite: 'ans', defaut: 3, min: 3, max: 10,
    depart: 'la fin du chantier (réception des travaux ou, à défaut, dernier événement d’un chantier terminé)',
    effet: 'Total d’heures gardé par chantier et par poste, sans nom ; les déclarations nominatives sont effacées.',
    fondement: 'Prescription de l’action en paiement des salaires : trois ans (Code du travail, article L3245-1) ; le temps passé '
      + 'peut être produit en cas de litige sur les heures (article L3171-4).',
  },
  connexion_mois: {
    cle: 'connexion_mois', libelle: 'Traces de connexion et journaux de sécurité', unite: 'mois', defaut: 12, min: 6, max: 12,
    depart: 'la date de chaque trace',
    effet: 'Connexions, vérifications du second facteur, changements et réinitialisations de mot de passe ou de second facteur, '
      + 'sessions expirées : effacés. Le reste du journal d’audit (qui a fait quoi sur les dossiers, devis et chantiers) est gardé '
      + 'comme preuve, avec les dossiers.',
    fondement: 'Recommandation de la CNIL sur la journalisation (14/10/2021) : six mois à un an. Les tentatives de connexion '
      + 'échouées sont, elles, effacées automatiquement au bout de 30 jours.',
  },
  comptes_mois: {
    cle: 'comptes_mois', libelle: 'Coordonnées des comptes désactivés', unite: 'mois', defaut: 12, min: 12, max: 36,
    depart: 'la désactivation du compte',
    effet: 'Adresse électronique et téléphone professionnel effacés (le compte ne peut plus être réactivé) ; le nom reste attaché '
      + 'à ce que la personne a fait sur les chantiers, aussi longtemps que leur historique.',
    fondement: 'Aucune durée imposée par la loi : un an laisse le temps de réactiver un compte (retour d’un salarié) ; au-delà, '
      + 'les coordonnées ne servent plus (RGPD, article 5.1.e).',
  },
  // Bons d'intervention (option, migrations 045 et 047). Sources : docs/REGLEMENTATION-BONS-INTERVENTION.md.
  bons_ans: {
    cle: 'bons_ans', libelle: 'Bons d’intervention (et fiches fluides)', unite: 'ans', defaut: 5, min: 5, max: 30,
    depart: 'la signature du bon (pour un bon jamais signé : sa dernière modification) ; un bon corrigé par avenant part avec toutes '
      + 'ses versions, à l’échéance de la plus récente',
    effet: 'Bon effacé avec toutes ses versions, ses photos, ses sauvegardes de programme et ses PDF signés ; le journal d’audit garde '
      + 'l’action et la date, attribuées à « Ancien salarié », sans détail.',
    fondement: 'Fiche d’intervention sur un équipement contenant des fluides frigorigènes : au moins cinq ans à compter de la '
      + 'signature (Code de l’environnement, article R543-82) — jamais moins pour un bon qui porte une fiche fluides, quel que soit '
      + 'le réglage. Autres bons : preuve de l’intervention pendant la prescription commerciale de cinq ans (Code de commerce, '
      + 'article L110-4).',
  },
};

/** Bon portant une fiche fluides : jamais effacé moins de 5 ans après sa signature (R543-82), quel que soit le réglage. */
export const BONS_FLUIDES_ANS_MIN = 5;

export const CLES_DUREES = Object.keys(DUREES) as CleDuree[];

/** Engagements du représentant légal : fin du contrat + 5 ans (non réglable : durée de preuve de l'éditeur). */
export const ENGAGEMENTS_ANS_APRES_CONTRAT = 5;
export const FONDEMENT_ENGAGEMENTS = 'Prescription entre commerçants : cinq ans (Code de commerce, article L110-4), à compter de la fin '
  + 'du contrat. Sans date de fin de contrat, rien n’est effacé.';

export type Durees = Record<CleDuree, number>;
export const DUREES_DEFAUT: Durees = Object.fromEntries(CLES_DUREES.map((k) => [k, DUREES[k].defaut])) as Durees;

/** Valeur stockée lue avec ses bornes : absente, illisible ou hors bornes → ramenée dans les bornes (jamais sous le plancher). */
export function dureeBornee(cle: CleDuree, v: unknown): number {
  const d = DUREES[cle];
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : NaN;
  if (!Number.isInteger(n)) return d.defaut;
  return Math.min(d.max, Math.max(d.min, n));
}

/** Libellé d'une durée (« 10 ans », « 12 mois », « 1 an »). */
export function libelleDuree(cle: CleDuree, n: number): string {
  if (DUREES[cle].unite === 'ans') return `${n} an${n > 1 ? 's' : ''}`;
  if (n % 12 === 0) return `${n / 12} an${n / 12 > 1 ? 's' : ''}`;
  return `${n} mois`;
}

/** Actions du journal d'audit traitées comme « traces de connexion et journaux de sécurité » (purgées à l'échéance). */
export const ACTIONS_SECURITE = ['connexion', 'mfa_verifie', 'mfa_active', 'mot_de_passe_modifie', 'mot_de_passe_reinitialise',
  'mfa_reinitialise', 'creation_compte_refusee'] as const;

/** Catégories comptées par un passage de purge (aucun contenu : des nombres). */
export type CategoriePurge =
  | 'photos' | 'consignes' | 'affectations' | 'historique_anonymise' | 'temps_agreges' | 'temps_effaces'
  | 'traces_connexion' | 'sessions_expirees' | 'comptes_pseudonymises' | 'engagements' | 'bons' | 'bons_fichiers';

export const LIBELLES_CATEGORIES: Record<CategoriePurge, string> = {
  photos: 'Photos de chantier effacées (fichier et ligne)',
  consignes: 'Consignes de relève effacées',
  affectations: 'Affectations du planning effacées',
  historique_anonymise: 'Chantiers dont l’historique est attribué à « Ancien salarié »',
  temps_agreges: 'Chantiers dont le temps passé est agrégé sans nom',
  temps_effaces: 'Déclarations de temps nominatives effacées',
  traces_connexion: 'Traces de connexion et de sécurité effacées',
  sessions_expirees: 'Sessions expirées effacées',
  comptes_pseudonymises: 'Comptes désactivés dont l’adresse et le téléphone sont effacés',
  engagements: 'Engagements du représentant légal effacés (contrat terminé depuis plus de 5 ans)',
  bons: 'Bons d’intervention effacés (toutes versions)',
  bons_fichiers: 'Fichiers de bons effacés (photos, sauvegardes, PDF signés)',
};

export const CATEGORIES_PURGE = Object.keys(LIBELLES_CATEGORIES) as CategoriePurge[];
export type Comptes = Record<CategoriePurge, number>;
export const comptesVides = (): Comptes => Object.fromEntries(CATEGORIES_PURGE.map((k) => [k, 0])) as Comptes;

/** Nom affiché du compte de remplacement d'une personne dont l'historique a été anonymisé. */
export const NOM_ANCIEN_SALARIE = 'Ancien salarié';
